<script>
import Countdown from '@homepage/components/Countdown.vue';
import ResultDisplay from '@homepage/components/pageStyles/superLotto/ResultDisplay.vue';
import CardPresenter from '@homepage/components/CardPresenter.vue';
import LotteryResultAutoDisplay from '@homepage/components/LotteryResultAutoDisplay.vue';
import LotteryResult from '@homepage/components/LotteryResult.vue';
import PrizesList from '@homepage/components/PrizesList.vue';
import { generateImageMapFor } from '@homepage/helpers/image.js';

export default {
	name: 'SuperLottoBoard',
	components: {
		ResultDisplay,
		Countdown,
		CardPresenter,
		LotteryResult,
		LotteryResultAutoDisplay,
		PrizesList
	},
	props: {
		generalInformation: {
			type: Object,
			required: true
		}
	},
	data() {
		return {
			timeToShowPrizesPage: 8000,
			timeToShowEachLotteryResultPage: 5000,
			lotteryEntriesPerPage: 2
		};
	},
	computed: {
		timeToShowResultsPage() {
			const timeToShowEachPage = this.timeToShowEachLotteryResultPage / 1000;

			return Math.ceil(this.generalInformation.lotteriesResultsEntries.length / this.lotteryEntriesPerPage) * timeToShowEachPage * 1000;
		}
	},
	provide() {
		return {
			imagesMap: generateImageMapFor('quickmoney'),
			generalInformation: this.generalInformation
		};
	},
	beforeCreate() {
		this.$emit('change-style', { backgroundImage: require('@/assets/img/superlotto/background.jpg') });
	}
};
</script>

<template>
	<v-row justify='center' no-gutters>
		<v-col cols='12'>
			<v-row justify='center' align='stretch'>
				<v-col
					v-if='generalInformation.results.slice(1).length > 0'
					cols='4'
					class='d-flex flex-column'
				>
					<!-- Next drawing card  -->
					<div class='card-header'><span>Pick</span> <span>Numbers</span></div>
					<v-card shaped class='secondary-card'>
						<v-card-text>
							<result-display
								v-for='result in generalInformation.results.slice(1, 3)'
								:key='result.sortition.id'
								:result='result'
							>
							</result-display>
						</v-card-text>
					</v-card>
				</v-col>
				<v-col cols='4' class='d-flex flex-column'>
					<!-- Next drawing card  -->
					<v-img class='align-self-center' width='250' height='100' contain
								 :src="require('assets/static/img/logo/superlotto_white.png')"></v-img>
					<v-card id='primary-card' class='flex-grow-1 transparent--bg' shaped>
						<v-card-text>
							<countdown
								id='upcoming-result'
								text-center
								class='mt-2 mb-2'
								:sortition='generalInformation.sortition'>
								<template #default='{sortition}'>
									<v-flex md3>
										<h1 class='font-weight-bold text-uppercase'>Next draw in</h1>
									</v-flex>
									<v-flex md3>
										<h2 id='next-sortition' class='font-weight-bold'>{{ sortition.name }}</h2>
									</v-flex>
									<v-flex md3>
										<span id='time-left' v-if='sortition.timeLeft'
													class='font-weight-black text-uppercase'>{{ sortition.timeLeft }}</span>
										<span v-else><font-awesome-icon icon='sync' spin size='3x'></font-awesome-icon></span>
									</v-flex>
								</template>
							</countdown>
							<hr />
							<template v-if='generalInformation.results.slice(0, 1).length > 0'>
								<h1 class='text-center my-4'>
									Winning numbers
								</h1>
								<result-display
									v-for='result in generalInformation.results.slice(0, 1)'
									:key='result.sortition.id'
									:result='result'
								>
								</result-display>
							</template>
						</v-card-text>
					</v-card>
				</v-col>
				<v-col cols='4' class='d-flex flex-column'>
					<card-presenter
						:time-per-page='[timeToShowPrizesPage, timeToShowResultsPage]'>
						<template #page-1>
							<div class='card-header'>PAYOUTS</div>
							<v-card class='secondary-card' shaped>
								<prizes-list :general-information='generalInformation' class='px-3 py-1' :rounded='false'>
									<template #prize-row='{prize}'>
										<v-col class='prize-row' cols='12'>
											<v-row no-gutters class='py-4'>
												<v-col class='prize-name text-center' cols='6'>
													{{ prize.name }} - 1X:
												</v-col>
												<v-col class='prize-amount' cols='6'>
													{{ prize.amount }}
												</v-col>
											</v-row>
										</v-col>
									</template>
								</prizes-list>
							</v-card>
						</template>
						<template #page-2>
							<div class='card-header'>RESULTS</div>
							<v-card id='lottery-results' class='secondary-card'>
								<lottery-result-auto-display
									:general-information='generalInformation'
									:entries-per-page='lotteryEntriesPerPage'
									:time-to-show-each-page='timeToShowEachLotteryResultPage'>
									<template #page='{ entries }'>
										<v-container class='px-0 py-0'>
											<lottery-result v-for='(entry, index) in entries'
																			class='lottery-result'
																			:class="{'rounded-b-xl': index === entries.length - 1}"
																			:show-pick-five='true'
																			:key='entry.name'
																			:lottery='entry'>
												<template #sortition-header='{sortition, icon, date}'>
															<span class='sortition-header'>
																<font-awesome-icon :icon='icon'></font-awesome-icon>
																{{ sortition.name }}: {{ date }}
															</span>
												</template>
											</lottery-result>
										</v-container>
									</template>
								</lottery-result-auto-display>
							</v-card>
						</template>
					</card-presenter>
				</v-col>
			</v-row>
		</v-col>
		<v-col id='page-url' cols='12' class='text-center'>
			<span>Visit our page suplotto.net</span>
		</v-col>
	</v-row>
</template>

<style scoped lang='scss'>
$card-background: rgba(#2b55bc, 0.5);

* {
	color: white;
	font-family: "Intro", "Liberation Sans Narrow", serif;
	//font-size: 16px;
}

@font-face {
	font-family: "Intro";
	src: url("../../../../public/fonts/Intro.otf");
}

@font-face {
	font-family: "Intro Inline";
	src: url("../../../../public/fonts/Intro Inline.otf");
}

.result-display {
	margin-bottom: 20px;

	&:last-child {
		margin-bottom: 0;
	}
}

::v-deep .sortition-result {
	padding: 8px 0;
}

::v-deep .sortition-result:first-child {
	padding-top: 12px;
}

::v-deep .sortition-result:last-child {
	padding-bottom: 12px;
}

.secondary-card {
	background-color: darken($card-background, 20) !important;
	box-shadow: 8px 6px 20px 0 #07000080;
	border-radius: 30px;
}

#primary-card {
	background-color: transparent;
	border: 2px solid white;
}

#time-left {
	line-height: 40px;
	font-weight: bold;
	font-size: 2.5rem;
	text-align: center;
	font-family: "Intro Inline", serif;
}

.prize-row {
	font-family: "Intro", serif;
	font-size: 1.7rem;
	line-height: 1.7rem;
	border: solid red 2px;
	border-radius: 15px;
	padding: 15px;
}

#upcoming-result h1 {
	font-size: 2.2rem;
}

.card-header {
	box-shadow: -3px 3px 7px 0px #0000009e;
	font-size: 1.5rem;
	padding: 0 6px;
	margin-bottom: 5px;
	margin-left: 20px;
	margin-right: 20px;
	display: flex;
	flex-direction: row;
	justify-content: space-around;
	border-radius: 30px;
	background-image: linear-gradient(to right, #fd383b, #c11d1e);
}

#page-url {
	font-size: 2rem;
}
</style>
